import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Subject, filter, firstValueFrom, lastValueFrom, of, switchMap } from "rxjs";
import { environment } from "@apps/patient-core/src/app/environments/environment";
import { PushUpdatesService } from "@apps/patient-core/src/app/services/push-updates.service";
import { E_MessageKind } from "@backend/graph/_services/e-message-kind";
import { generateNonce } from "@dentally/auth/browser-utils";
import { BaseCommonService } from "@apps/patient-core/src/app/services/abstracts/common.service";
import { NavigationService } from "@apps/patient-core/src/app/services/navigation.service";
import { CacheService } from "@apps/patient-core/src/app/services/cache.service";
import { WindowService } from "@apps/patient-core/src/app/services/window.service";
import { SubSink } from "subsink";
import { LocationService } from "@apps/patient-core/src/app/services/location.service";
import { ReferrerService } from "@apps/patient-core/src/app/services/referrer.service";
import { E_Referrer } from "@backend/common/enums/referrer";
import { setThemeColor } from "@apps/patient-core/src/app/utils/theme";
import { I_ConciergeData, ConciergeDataService } from "./concierge-data.service";
import { NotificationInstance, NotificationService, NotificationTypes } from "@apps/patient-core/src/app/services/notification.service";
import { BasePatientsService } from "@apps/patient-core/src/app/services/abstracts/patients.service";
import { BaseBrandService } from "@apps/patient-core/src/app/services/abstracts/brand.service";
import { themeColour } from "../utils/theme";
import { Constants as CoreConstants } from "@apps/patient-core/constants";
import { isE2E, isPrStage } from "@shared/utils";
import { filterDefined } from "@apps/patient-core/src/app/utils/rxjs";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { ConciergeSpecificAuthService } from "./concierge-specific-auth.service";
import { AuthSessionType } from "@dentally/auth/auth-types";
import { Constants } from "@apps/concierge-app/constants";
import { logger } from "@dentally/ui/logger";

const { HOST_ZONE } = environment;
export const DEVICES_PATH = "/api/v1/devices";

export type DiagnosticsData = {
  id: string;
  practice_id: string;
  site_id: string;
  jwt_created_at: string;
  jwt_expiry: string;
};

@Injectable({
  providedIn: "root",
})
export class ConciergeService {
  private _activationCode: string;
  private _practiceName: string;
  private _siteId: string;
  private _siteName: string;
  private _siteUrl: string;
  private _subs = new SubSink();
  private _deviceLocked = false;
  private _nonce: string;
  public onPaired: Subject<I_ConciergeData> = new Subject<I_ConciergeData>();
  public hadPairingError = false;

  constructor(
    private _http: HttpClient,
    private _authService: BaseAuthService,
    private _pushUpdatesService: PushUpdatesService,
    private _commonService: BaseCommonService,
    private _navigationService: NavigationService,
    private _cacheService: CacheService,
    private _windowService: WindowService,
    private _locationService: LocationService,
    private _brandService: BaseBrandService,
    private _referrerService: ReferrerService,
    private _patientsService: BasePatientsService,
    private _conciergeDataService: ConciergeDataService,
    private _notificationService: NotificationService,
    private _conciergeSpecificAuthService: ConciergeSpecificAuthService
  ) {
    this._subs.sink = this._authService.onJWTChanged
      .pipe(
        filterDefined(),
        filter(({ status, jwt }) => status === "UPDATED" && this._authService.isPublicInPractice(jwt.access_level))
      )
      .subscribe(() => {
        this._pushUpdatesService.reconnect();
      });
  }

  public get nonce(): string {
    if (!this._nonce) {
      this._nonce = generateNonce();
    }

    return this._nonce;
  }

  public get activationCode(): string {
    return this._activationCode;
  }

  public get practiceName(): string {
    return this._practiceName;
  }

  public get siteId(): string {
    return this._siteId;
  }

  public get siteName(): string {
    return this._siteName;
  }

  public get siteUrl(): string {
    return this._siteUrl;
  }

  public get isDevicePaired(): boolean {
    return !!this._conciergeDataService.getConciergeData();
  }

  public onPageUnload(): void {
    this._subs.unsubscribe();
  }

  public get deviceId(): string | null {
    return this._authService.getJwtField("device_id");
  }

  public get deviceMode(): AuthSessionType {
    // Refresh tokens created before adding KIOSK mode won't have a type field, so default to CONCIERGE
    return this._authService.getJwtField<AuthSessionType>("type") || AuthSessionType.CONCIERGE;
  }

  public get practiceId(): string | null {
    return this._authService.getJwtField("practice_id");
  }

  /**
   * Generate a public JWT so that the Pusher connection can be authenticated
   */
  public async initForPairing(): Promise<void> {
    const response = (await lastValueFrom(
      this._http.post(
        DEVICES_PATH,
        {
          nonce: this.nonce,
        },
        {
          withCredentials: true,
        }
      )
    )) as { token: string; activationCode: string };
    const { token, activationCode } = response;

    this._authService.setPublicToken(token, true);
    this._activationCode = activationCode;
    this._subscribeToPusher();
    setThemeColor(themeColour);
  }

  public async initForPaired(): Promise<void> {
    await lastValueFrom(
      this._http.get(DEVICES_PATH, {
        withCredentials: true,
      })
    );
    this._referrerService.referrer = E_Referrer.PORTAL_IN_PRACTICE;
    this._subscribeToPusher();
    this._cacheService.delete(Constants.INACTIVITY_TIMER_END_TIME_STORAGE_KEY);
  }

  private _onUnlink(): void {
    logger.info("Device unlinked");
    this._conciergeDataService.deleteConciergeData();
    this._cacheService.delete(CoreConstants.RESTRICTED_SITE_STORAGE_KEY);
    this._cacheService.delete(CoreConstants.BRAND_INFO_STORAGE_KEY);
  }

  private _subscribeToPusher(): void {
    this._subs.sink = this._pushUpdatesService.onConnected
      .pipe(
        switchMap(() => {
          const device_id = this._authService.getJWT("device_id");
          if (typeof device_id !== "string") {
            logger.error("Invalid device_id", { jwt: this._authService.getJwtSafe() });
            return of({});
          }
          return this._pushUpdatesService
            .createPrivateChannelObservable(device_id)
            .pipe(
              filter((data) =>
                [
                  E_MessageKind.Device_Linked,
                  E_MessageKind.Device_Unlinked,
                  E_MessageKind.Device_Locked,
                  E_MessageKind.Device_Unlocked,
                  E_MessageKind.Device_Pinged,
                ].includes(data.message_kind)
              )
            );
        })
      )
      .subscribe(async (data) => {
        switch (data.message_kind) {
          case E_MessageKind.Device_Linked:
            try {
              const { jwt } = data.message_content as I_ConciergeData;

              await this._handleDeviceLinked(jwt, this.nonce);
              this._processConciergeData(data.message_content);

              this._conciergeDataService.setConciergeData(data.message_content);
            } catch (error) {
              logger.error("Failed to link device", { error });
              this._navigationService.navigate("/error", { state: { error: "Failed to link device" } });
            }

            break;

          case E_MessageKind.Device_Unlinked:
            this._onUnlink();
            await this._handleDeviceUnlinked();
            this._windowService.open("/pair");

            break;

          case E_MessageKind.Device_Locked:
            this._navigationService.navigate("/login/locked");

            break;

          case E_MessageKind.Device_Pinged:
            const { username } = data.message_content;
            const siteNameString = this.siteName ? ` at ${this.siteName}` : "";
            this._notificationService.open(
              new NotificationInstance({
                title: "Connection test",
                body: `Performed by ${username}${siteNameString}`,
                type: NotificationTypes.INFO,
                timeout: 0, // Persistent notification
              })
            );
            break;

          case E_MessageKind.Device_Unlocked:
            this.deviceLocked = false;
            this._navigationService.navigate("/login");

            break;
        }
      });
  }

  protected async _handleDeviceLinked(jwt: string, nonce: string): Promise<void> {
    const response = (await lastValueFrom(this._conciergeSpecificAuthService.handleDeviceLinked(jwt, nonce))) as { accessToken: string };

    if (!response?.accessToken) {
      throw new Error("Failed to link device");
    }

    this._authService.setToken(response.accessToken);
  }

  protected async _handleDeviceUnlinked(): Promise<void> {
    logger.info("Unlinking device");

    return this._conciergeSpecificAuthService.delete();
  }

  public async restoreConciergeData(): Promise<boolean> {
    const data = this._conciergeDataService.getConciergeData();

    if (data) {
      this._processConciergeData(data);

      // Make sure we get the patient before restoring the Concierge data to ensure the brand is set correctly
      if (this._authService.isPatient()) {
        // The patient must of refreshed the page while completing their forms so we need to load their data again
        // to prevent in infinite loop caused by the call to reload() in AppComponent when there is a patient JWT
        // but no patient info
        await this._patientsService.getPatient();
      }

      await this._loadBrandData();

      return true;
    }

    return false;
  }

  private _processConciergeData(data: I_ConciergeData): void {
    const { practiceName, siteId, siteName, siteUrl, stage } = data;
    const url = new URL(siteUrl);

    if (!practiceName || !siteId || !siteName || !siteUrl || !stage) {
      logger.error("Invalid concierge data", { data });
    }

    this._locationService.hostname = url.hostname;

    window.STAGE = stage;
    this._practiceName = practiceName;
    this._siteId = siteId;
    this._siteName = siteName;
    this._siteUrl = siteUrl;

    this.onPaired.next(data);
  }

  public get pairDomain(): string {
    const includeStage = window.STAGE && (isPrStage(window.STAGE) || isE2E(window.STAGE));
    const stage = includeStage ? `${window.STAGE}.` : "";
    return `https://pair.${stage}${HOST_ZONE}`;
  }

  public set deviceLocked(value: boolean) {
    this._deviceLocked = value;
  }

  public get deviceLocked(): boolean {
    return this._deviceLocked;
  }

  public get diagnosticsData(): DiagnosticsData | null {
    return {
      id: this.deviceId || "unknown",
      practice_id: this.practiceId || "unknown",
      site_id: this.siteId || "unknown",
      // Given how often we create new JWTs for Concierge this will be accurate enough to represent the last heartbeat. It also means we will have the diagnostics data to help debug app loading issues
      jwt_created_at: this._authService.jwtCreatedAtFormatted || "unknown",
      jwt_expiry: this._authService.jwtExpiryFormatted || "unknown",
    };
  }

  public async continueToLogin(): Promise<void> {
    await this._loadBrandData();

    this._navigationService.navigate("/login");
  }

  public navigateToGetStarted(): void {
    this._locationService.href = `/login/${Date.now()}`;
  }

  private async _waitForCommonData() {
    const promise = firstValueFrom(this._commonService.onInitData);
    this._commonService.getCommonData(true);

    await promise;
  }

  private async _loadBrandData(): Promise<void> {
    await this._commonService.getDomainInfo();
    await this._waitForCommonData();

    this._brandService.restrictedSiteId = this._siteId;
  }
}
